/**
 * One tone per status (STATE-003, TMMRSL-0057). The label keys already exist; this file only
 * assigns the tone. An absent or unknown status is not listed — `StatusChip` renders "—".
 *
 */

export type Tone = 'success' | 'info' | 'warning' | 'error' | 'neutral' | 'draft';

export interface StatusStyle {
  tone: Tone;
  labelKey: string;
}

function style(tone: Tone, labelKey: string): StatusStyle {
  return { tone, labelKey };
}

export const STATUS = {
  rateCard: {
    DRAFT: style('draft', 'rateCard.status.DRAFT'),
    ACTIVE: style('success', 'rateCard.status.ACTIVE'),
    CLOSED: style('neutral', 'rateCard.status.CLOSED'),
    NONCOMPLIANT: style('warning', 'rateCard.status.noncompliant'),
    ADOPTED: style('success', 'rateCard.status.ADOPTED'),
  },
  standing: {
    GOOD: style('success', 'agreement.standing.state.GOOD'),
    PROBATION: style('warning', 'agreement.standing.state.PROBATION'),
    DEACTIVATION_DUE: style('error', 'agreement.standing.state.DEACTIVATION_DUE'),
    DEACTIVATED: style('neutral', 'agreement.standing.state.DEACTIVATED'),
  },
  notice: {
    PENDING: style('info', 'notices.status.PENDING'),
    SENDING: style('info', 'notices.status.SENDING'),
    SENT: style('success', 'notices.status.SENT'),
    SKIPPED: style('neutral', 'notices.status.SKIPPED'),
    FAILED: style('error', 'notices.status.FAILED'),
  },
  resellerCharge: {
    PENDING: style('info', 'charges.status.PENDING'),
    REQUESTED: style('info', 'charges.status.REQUESTED'),
    SUCCEEDED: style('success', 'charges.status.SUCCEEDED'),
    FAILED: style('error', 'charges.status.FAILED'),
    REQUIRES_ACTION: style('warning', 'charges.status.REQUIRES_ACTION'),
  },
  card: {
    ACTIVE: style('success', 'resellerBilling.card.status.ACTIVE'),
    EXPIRED: style('error', 'resellerBilling.card.status.EXPIRED'),
    requires_action: style('warning', 'resellerBilling.card.status.requires_action'),
  },
  connect: {
    CREATING: style('info', 'payouts.connect.status.CREATING'),
    ONBOARDING: style('warning', 'payouts.connect.status.ONBOARDING'),
    ACTIVE: style('success', 'payouts.connect.status.ACTIVE'),
    RESTRICTED: style('error', 'payouts.connect.status.RESTRICTED'),
    DISABLED: style('neutral', 'payouts.connect.status.DISABLED'),
  },
  payout: {
    PAID: style('success', 'statements.status.PAID'),
    CARRIED: style('neutral', 'statements.status.CARRIED'),
    PENDING: style('info', 'statements.status.PENDING'),
    RETAINED: style('neutral', 'payouts.status.RETAINED'),
    CHARGED_SUCCEEDED: style('info', 'statements.status.CHARGED'),
    CHARGED_FAILED: style('error', 'statements.status.CHARGED'),
    CHARGED: style('warning', 'statements.status.CHARGED'),
    CHARGED_SUCCEEDED_NO_AMOUNT: style('info', 'statements.status.CHARGEDNoAmount'),
    CHARGED_FAILED_NO_AMOUNT: style('error', 'statements.status.CHARGEDNoAmount'),
    CHARGED_NO_AMOUNT: style('warning', 'statements.status.CHARGEDNoAmount'),
  },
  statement: {
    NONE: style('neutral', 'subscribers.chargeStatus.NONE'),
    PENDING: style('info', 'subscribers.chargeStatus.PENDING'),
    PAID: style('success', 'subscribers.chargeStatus.PAID'),
    ACTION_REQUIRED: style('warning', 'subscribers.chargeStatus.ACTION_REQUIRED'),
    FAILED: style('error', 'subscribers.chargeStatus.FAILED'),
    ATTENTION: style('warning', 'subscribers.chargeStatus.ATTENTION'),
    WAIVED: style('neutral', 'subscribers.chargeStatus.WAIVED'),
  },
  addon: {
    OFFERED: style('warning', 'addons.status.OFFERED'),
    ACTIVE: style('success', 'addons.status.ACTIVE'),
    ACTIVE_CANCEL_AT_TERM_END: style('info', 'addons.endsAtTermEnd'),
    PAST_DUE: style('warning', 'addons.status.PAST_DUE'),
    LAPSED: style('neutral', 'addons.status.LAPSED'),
    CANCELLED: style('neutral', 'addons.status.CANCELLED'),
    DECLINED: style('error', 'addons.status.DECLINED'),
    WITHDRAWN: style('neutral', 'addons.status.WITHDRAWN'),
  },
  addonCharge: {
    FAILED: style('error', 'addons.charge.status.FAILED'),
    ACTION_REQUIRED: style('warning', 'addons.charge.status.ACTION_REQUIRED'),
    PAID: style('success', 'addons.charge.status.PAID'),
    NETTED: style('success', 'addons.charge.status.NETTED'),
    WAIVED: style('neutral', 'addons.charge.status.WAIVED'),
    PENDING: style('info', 'addons.charge.status.PENDING'),
    NETTING: style('info', 'addons.charge.status.NETTING'),
    REFUNDED: style('neutral', 'addons.charge.status.REFUNDED'),
  },
  affiliateHold: { REVIEW: style('warning', 'affiliateHold.REVIEW') },
  affiliate: {
    APPLIED: style('warning', 'affiliates.status.APPLIED'),
    ACTIVE: style('success', 'affiliates.status.ACTIVE'),
    PAUSED: style('neutral', 'affiliates.status.PAUSED'),
    ENDED: style('neutral', 'affiliates.status.ENDED'),
  },
  affiliatePayout: {
    taxDocsPending: style('warning', 'affiliates.payout.taxDocsPending'),
    ready: style('success', 'affiliates.payout.ready'),
    manual: style('neutral', 'affiliates.payout.manual'),
  },
  subscriberPending: {
    PENDING: style('info', 'subscribers.pending.PENDING'),
    COMPLETED: style('success', 'subscribers.pending.COMPLETED'),
    EXPIRED: style('error', 'subscribers.pending.EXPIRED'),
    CANCELLED: style('neutral', 'subscribers.pending.CANCELLED'),
  },
  subscriber: {
    active: style('success', 'subscribers.status.active'),
    trialing: style('info', 'subscribers.status.trialing'),
    past_due: style('warning', 'subscribers.status.past_due'),
    paused: style('neutral', 'subscribers.status.paused'),
    comped: style('info', 'subscribers.status.comped'),
    canceled: style('neutral', 'subscribers.status.canceled'),
    creating: style('info', 'subscribers.status.creating'),
  },
  seat: {
    ACTIVE: style('success', 'subscribers.seats.statusValue.ACTIVE'),
    ENDING: style('info', 'subscribers.seats.statusValue.ENDING'),
    ENDED: style('neutral', 'subscribers.seats.statusValue.ENDED'),
  },
  promotion: {
    ACTIVE: style('success', 'promotions.status.ACTIVE'),
    PAUSED: style('neutral', 'promotions.status.PAUSED'),
    ENDED: style('neutral', 'promotions.status.ENDED'),
  },
  deletion: {
    PENDING: style('info', 'settings.data.status.PENDING'),
    ERASING: style('info', 'settings.data.status.ERASING'),
    FAILED: style('error', 'settings.data.status.FAILED'),
    ERASED: style('neutral', 'settings.data.status.ERASED'),
    CANCELLED: style('neutral', 'settings.data.status.CANCELLED'),
  },
  crm: {
    AVAILABLE: style('success', 'settings.integrations.status.AVAILABLE'),
    UNAVAILABLE: style('error', 'settings.integrations.status.UNAVAILABLE'),
    NOT_CONFIGURED: style('neutral', 'settings.integrations.status.NOT_CONFIGURED'),
  },
  delivery: {
    PENDING: style('info', 'settings.integrations.delivery.PENDING'),
    SENT: style('success', 'settings.integrations.delivery.SENT'),
    FAILED: style('error', 'settings.integrations.delivery.FAILED'),
    SKIPPED: style('neutral', 'settings.integrations.delivery.SKIPPED'),
  },
  tenant: {
    ACTIVE: style('success', 'tenant.status.ACTIVE'),
    SUSPENDED: style('neutral', 'tenant.status.SUSPENDED'),
  },
  code: {
    ACTIVE: style('success', 'affiliates.code.ACTIVE'),
    PAUSED: style('neutral', 'affiliates.code.PAUSED'),
  },
  dns: {
    VERIFIED: style('success', 'domain.records.verified'),
    PENDING: style('info', 'domain.records.pending'),
  },
  ghl: {
    CONNECTED: style('success', 'ghl.connected'),
    NOT_CONNECTED: style('neutral', 'ghl.notConnected'),
    INACTIVE: style('neutral', 'ghl.inactive'),
  },
  referrals: {
    ACTIVE: style('success', 'affiliates.referrals.status.ACTIVE'),
    ENDED: style('neutral', 'affiliates.referrals.status.ENDED'),
    SUPERSEDED: style('neutral', 'affiliates.referrals.status.SUPERSEDED'),
    VOIDED: style('neutral', 'affiliates.referrals.status.VOIDED'),
  },
  member: {
    ACTIVE: style('success', 'members.status.ACTIVE'),
    SUSPENDED: style('neutral', 'members.status.SUSPENDED'),
    INVITED: style('info', 'members.status.INVITED'),
    DISABLED: style('neutral', 'members.status.DISABLED'),
  },
  invitation: {
    PENDING: style('info', 'members.invitationStatus.PENDING'),
    ACCEPTED: style('success', 'members.invitationStatus.ACCEPTED'),
    // An owner ended access (the reseller revoked it): neutral, "Removed" (profile 1.1 §5).
    REVOKED: style('neutral', 'members.invitationStatus.REVOKED'),
    EXPIRED: style('error', 'members.invitationStatus.EXPIRED'),
  },
  // Partner ledger statement statuses (TMMRSL-0080, TMMPDM-0095 Decision 8). OTHER is the generic
  // label for a status this console doesn't know; the raw value stays on the view.
  ledgerStatement: {
    CARRIED: style('neutral', 'ledger.statementStatus.CARRIED'),
    HELD: style('warning', 'ledger.statementStatus.HELD'),
    NEGATIVE: style('warning', 'ledger.statementStatus.NEGATIVE'),
    PAYABLE: style('info', 'ledger.statementStatus.PAYABLE'),
    PAYOUT_FAILED: style('error', 'ledger.statementStatus.PAYOUT_FAILED'),
    COLLECTING: style('info', 'ledger.statementStatus.COLLECTING'),
    PAYING: style('info', 'ledger.statementStatus.PAYING'),
    PAID: style('success', 'ledger.statementStatus.PAID'),
    PAID_EXTERNALLY: style('success', 'ledger.statementStatus.PAID_EXTERNALLY'),
    COLLECTED: style('success', 'ledger.statementStatus.COLLECTED'),
    SETTLED: style('success', 'ledger.statementStatus.SETTLED'),
    OTHER: style('neutral', 'ledger.statementStatus.OTHER'),
  },
  // Hold kinds (TMMPDM-0095 Decisions 8 and 15). PAYOUT_RETURNED is a failed payout (error);
  // OTHER is still a hold (warning). COLLECTION_CONFLICT waits on someone, so warning.
  ledgerHold: {
    FOR_CAUSE: style('warning', 'ledger.holdKind.FOR_CAUSE'),
    REVIEW: style('warning', 'ledger.holdKind.REVIEW'),
    TAX_ID_UNVERIFIED: style('warning', 'ledger.holdKind.TAX_ID_UNVERIFIED'),
    PAYOUT_RETURNED: style('error', 'ledger.holdKind.PAYOUT_RETURNED'),
    COLLECTION_CONFLICT: style('warning', 'ledger.holdKind.COLLECTION_CONFLICT'),
    OTHER: style('warning', 'ledger.holdKind.OTHER'),
  },
  hold: {
    PAYOUTS_HELD: style('warning', 'resellerBilling.charges.payoutsHeld'),
    HOLD_TAX_DOCS: style('warning', 'statements.hold.taxDocs'),
    HOLD_STRIPE_CONNECT: style('warning', 'statements.hold.stripeConnect'),
    HOLD_TRANSFER_CURRENCY: style('warning', 'statements.hold.transferCurrency'),
    HOLD_TRANSFER_CAP: style('warning', 'statements.hold.transferCap'),
    HOLD_RESELLER_UNPAID: style('warning', 'statements.hold.resellerUnpaid'),
  },
  transfer: {
    FAILED: style('error', 'statements.transfer.FAILED'),
    REJECTED: style('error', 'statements.transfer.REJECTED'),
  },
} as const satisfies Record<string, Record<string, StatusStyle>>;

export type StatusDomain = keyof typeof STATUS;

/** A payout's CHARGED tone follows the charge: succeeded is info, failed is error, otherwise warning. */
export function chargedPayoutStatus(
  chargeStatus: string | null | undefined,
  hasAmount: boolean,
): keyof typeof STATUS.payout {
  const base =
    chargeStatus === 'SUCCEEDED'
      ? 'CHARGED_SUCCEEDED'
      : chargeStatus === 'FAILED'
        ? 'CHARGED_FAILED'
        : 'CHARGED';
  return hasAmount ? base : `${base}_NO_AMOUNT`;
}

export function statusStyle(
  domain: StatusDomain,
  status: string | null | undefined,
): StatusStyle | null {
  if (!status) return null;
  const entry: StatusStyle | undefined =
    STATUS[domain][status as keyof (typeof STATUS)[StatusDomain]];
  return entry ?? null;
}
